import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { FontPreview } from '../components/FontPreview';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, Download, CreditCard, Type, Check, Image as ImageIcon, Loader2 } from 'lucide-react';
import { ScrollAnimateIn, useScrollAnimation } from '../hooks/useScrollAnimation';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const navigate = useNavigate();
  const [shopItems, setShopItems] = useState([]);
  const [loadingShop, setLoadingShop] = useState(true);

  // Fetch 12 latest shop items (Digital Posters only) on mount
  useEffect(() => {
    const fetchShopItems = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/shop/items?limit=12&type=poster`);
        setShopItems(response.data.items || []);
      } catch (error) {
        console.error('Failed to fetch shop items:', error);
      } finally {
        setLoadingShop(false);
      }
    };
    fetchShopItems();
  }, []);

  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Creation',
      description: 'Describe your vision and let AI craft unique typography instantly',
    },
    {
      icon: Type,
      title: 'Live Preview',
      description: 'See your custom font rendered in real-time as you create',
    },
    {
      icon: Download,
      title: 'Hi-Res Downloads',
      description: 'Get stunning 5000×5000px preview images of your custom fonts',
    },
    {
      icon: CreditCard,
      title: 'Affordable Pricing',
      description: 'Just $1.99 per high-resolution font preview download',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0D0507]" data-testid="landing-page">
      <Header />
      
      {/* Hero Section - Gothic Cathedral */}
      <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden stained-glass-bg">
        {/* Gothic Decorative Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Stained glass radial effect */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] opacity-20">
            <div className="absolute inset-0 bg-gradient-radial from-[#722F37]/40 via-transparent to-transparent rounded-full" />
          </div>
          
          {/* Gold accent lines */}
          <div className="absolute top-20 left-[10%] w-32 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
          <div className="absolute top-32 right-[15%] w-24 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />
          <div className="absolute bottom-40 left-[20%] w-20 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/25 to-transparent" />
          
          {/* Corner ornaments */}
          <div className="absolute top-24 left-8 w-2 h-2 rounded-full bg-[#D4AF37]/30" />
          <div className="absolute top-40 right-12 w-3 h-3 rounded-full bg-[#722F37]/40" />
          <div className="absolute bottom-32 left-16 w-2 h-2 rounded-full bg-[#D4AF37]/20" />
        </div>
        
        <div className="max-w-6xl mx-auto relative">
          <ScrollAnimateIn animation="fade-up" className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 wine-glass rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-cinzel font-medium tracking-wider text-[#D4AF37]">AI-Powered Typography</span>
            </div>
            
            <h1 className="hero-text mb-6">
              Forge Sacred Gothic Typography
            </h1>
            
            <p className="text-lg text-[#C2B49A] max-w-2xl mx-auto mb-10 leading-relaxed font-serif">
              Transform your ideas into unique typefaces in seconds. Simply describe your vision, 
              and FuntzGuntzAI generates beautiful custom fonts ready for download.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={() => navigate('/generator')}
                className="btn-primary rounded-lg group"
                data-testid="hero-cta-btn"
              >
                Start Creating Free
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/shop')}
                className="btn-secondary rounded-lg"
                data-testid="hero-shop-btn"
              >
                Shop Digital Posters
              </Button>
            </div>
          </ScrollAnimateIn>

          {/* Preview Card - Gothic Parchment */}
          <ScrollAnimateIn animation="fade-up-scale" delay={200} className="mt-16 max-w-4xl mx-auto">
            <div className="card-surface rounded-lg p-8 md:p-12">
              <div className="text-center space-y-6">
                <p className="mono-label">Live Preview</p>
                <div className="space-y-4">
                  <p className="text-4xl md:text-5xl font-gothic text-[#F4EFE6]">
                    Elegant Gothic
                  </p>
                  <p className="text-2xl md:text-3xl italic text-[#C2B49A] font-serif">
                    Beautiful Serif
                  </p>
                  <p className="text-xl md:text-2xl tracking-widest text-[#C5A059] font-mono uppercase">
                    Technical Mono
                  </p>
                </div>
              </div>
            </div>
          </ScrollAnimateIn>
        </div>
      </section>

      {/* Features Grid - Gothic Cards */}
      <section className="py-24 px-6 lg:px-8 bg-[#160A0D]">
        <div className="max-w-6xl mx-auto">
          <ScrollAnimateIn animation="fade-up" className="text-center mb-16">
            <p className="mono-label mb-3">How It Works</p>
            <h2 className="text-3xl md:text-4xl font-cinzel font-semibold text-[#F4EFE6]">
              Create fonts in four simple steps
            </h2>
          </ScrollAnimateIn>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <ScrollAnimateIn 
                key={feature.title}
                animation="fade-up"
                delay={index * 100}
              >
                <div 
                  className="card-surface p-8 rounded-lg hover-lift h-full"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center gothic-border rounded-lg mb-6">
                    <feature.icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-cinzel font-semibold mb-3 text-[#F4EFE6]">{feature.title}</h3>
                  <p className="text-[#C2B49A] text-sm leading-relaxed font-serif">{feature.description}</p>
                </div>
              </ScrollAnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* Hi-Res Download Promo Banner - Wine Gold Gradient */}
      <section className="py-16 px-6 lg:px-8 bg-gradient-to-r from-[#722F37] via-[#5A252C] to-[#3D1A1F]">
        <div className="max-w-6xl mx-auto">
          <ScrollAnimateIn animation="fade-in">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/20 rounded-full mb-4 border border-[#D4AF37]/30">
                  <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-sm font-cinzel font-medium text-[#D4AF37]">Premium Quality</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-cinzel font-bold text-[#F4EFE6] mb-3">
                  Download Hi-Res Font Previews
                </h2>
                <p className="text-[#F4EFE6]/80 text-lg max-w-xl font-serif">
                  Get stunning 5000×5000px digital posters of your custom fonts. 
                  Perfect for wall art, social media, or design inspiration.
                </p>
              </div>
              <div className="flex flex-col items-center gap-4">
                <div className="bg-[#0D0507] rounded-lg p-6 text-center shadow-2xl border border-[#D4AF37]/30">
                  <p className="text-sm text-[#C2B49A] mb-1 font-serif">Only</p>
                  <p className="text-5xl font-cinzel font-bold text-[#D4AF37]">$1.99</p>
                  <p className="text-sm text-[#C2B49A] mt-1 font-serif">per download</p>
                </div>
                <Button
                  onClick={() => navigate('/generator')}
                  className="bg-[#D4AF37] text-[#0D0507] hover:bg-[#C5A059] px-8 py-6 text-base font-cinzel font-medium rounded-lg shadow-lg group"
                  data-testid="promo-banner-cta"
                >
                  Start Creating
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </ScrollAnimateIn>
        </div>
      </section>

      {/* Shop Preview Section - Sacred Gallery */}
      <section className="py-24 px-6 lg:px-8 bg-[#0D0507]">
        <div className="max-w-6xl mx-auto">
          <ScrollAnimateIn animation="fade-up" className="text-center mb-12">
            <p className="mono-label mb-3">Featured Collection</p>
            <h2 className="text-3xl md:text-4xl font-cinzel font-bold text-[#F4EFE6] mb-4">
              Digital Font Posters
            </h2>
            <p className="text-[#C2B49A] max-w-xl mx-auto font-serif">
              High-resolution 5000×5000px typography posters, ready for instant download. 
              Perfect for wall art, social media, or design inspiration.
            </p>
          </ScrollAnimateIn>

          {loadingShop ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative w-12 h-12 mb-4">
                <svg
                  className="animate-gothic-spin"
                  viewBox="0 0 50 50"
                  style={{ width: 48, height: 48 }}
                >
                  <circle
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    stroke="url(#landingGradient)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="80 40"
                  />
                  <defs>
                    <linearGradient id="landingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#D4AF37" />
                      <stop offset="50%" stopColor="#C5A059" />
                      <stop offset="100%" stopColor="#722F37" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg text-[#D4AF37] animate-candle">✦</span>
                </div>
              </div>
              <p className="font-serif text-[#C2B49A] text-sm">Loading collection...</p>
            </div>
          ) : shopItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {shopItems.map((item, index) => (
                <ScrollAnimateIn 
                  key={item.id || index}
                  animation="fade-up"
                  delay={index * 80}
                >
                  <div 
                    className="card-surface rounded-lg overflow-hidden hover-lift group cursor-pointer h-full"
                    onClick={() => navigate('/shop')}
                    data-testid={`shop-preview-item-${index}`}
                  >
                    {/* Preview */}
                    <div className="p-4 bg-[#160A0D]">
                      <FontPreview 
                        font={{
                          id: item.font_id,
                          preview_text: item.preview_text,
                          css_properties: item.css_properties,
                          style_properties: item.style_properties
                        }}
                        borderStyle={item.border_style || 'none'}
                        borderColor={item.border_color_hex || '#D4AF37'}
                      />
                    </div>
                    
                    {/* Details */}
                    <div className="p-5 bg-[#220F13]">
                      <div className="flex items-center gap-2 mb-2">
                        <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-xs font-cinzel font-medium text-[#D4AF37] uppercase tracking-wider">
                          Digital Poster
                        </span>
                        <span className="ml-auto text-lg font-cinzel font-bold text-[#D4AF37]">
                          ${item.price?.toFixed(2) || '1.99'}
                        </span>
                      </div>
                    
                    <h3 className="text-lg font-cinzel font-semibold mb-1 text-[#F4EFE6] group-hover:text-[#D4AF37] transition-colors">
                      {item.title || 'Digital Poster'}
                    </h3>
                    <p className="text-sm text-[#C2B49A] line-clamp-2 font-serif">
                      5000×5000px Hi-Res Download
                    </p>
                  </div>
                </div>
                </ScrollAnimateIn>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 card-surface rounded-lg border border-dashed border-[#D4AF37]/30 mb-10">
              <Type className="w-12 h-12 text-[#C2B49A] mx-auto mb-4" />
              <p className="text-[#C2B49A] mb-2 font-serif">Shop is being stocked...</p>
              <p className="text-sm text-[#C2B49A]/60 font-serif">Create fonts to add items to the shop!</p>
            </div>
          )}

          <div className="text-center">
            <Button
              onClick={() => navigate('/shop')}
              variant="outline"
              className="btn-secondary rounded-lg"
              data-testid="view-all-shop-btn"
            >
              View All in Shop
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - Cathedral */}
      <section className="py-24 px-6 lg:px-8 stained-glass-bg">
        <ScrollAnimateIn animation="scale-in" className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-gothic mb-6 text-[#F4EFE6]">
            Ready to create?
          </h2>
          <p className="text-lg text-[#C2B49A] mb-10 font-serif">
            Join thousands of designers creating unique fonts with AI
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="btn-primary px-12 py-6 text-lg rounded-lg animate-pulse-glow"
            data-testid="cta-signup-btn"
          >
            Get Started Free
          </Button>
        </ScrollAnimateIn>
      </section>

      {/* Footer - Gothic */}
      <footer className="py-12 px-6 lg:px-8 border-t border-[#D4AF37]/20 bg-[#0D0507]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <span className="text-lg font-cinzel font-bold text-[#F4EFE6]">
              Funtz<span className="text-[#D4AF37]">Guntz</span>AI
            </span>
          </div>
          <p className="text-sm text-[#C2B49A] font-serif">
            © 2024 FuntzGuntzAI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
